import { getUserInfo, updateUserClaims } from "@payping/redux/actions";
import { Button, Typography } from "@payping/web-components";
import React, { lazy, useEffect, useState } from "react";
import { useAsync } from "react-use";

import { removeLottieLoading, userManager } from "@/utils";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import componentLoader from "@/utils/componentLoader";
import { useAppTranslation } from "@/utils/useAppTranslation";
import Loading from "./loading";

const Main = lazy(() => componentLoader(() => import(/* webpackPreload: true */ "./index"), 3));

const InitApp: React.FC = () => {
  const [element, setElement] = useState<JSX.Element>(<Loading />);
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const token = useAppSelector((store) => store.auth.token);
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();

  const { loading } = useAsync(async () => {
    try {
      const userInfo = await dispatch(getUserInfo());

      if (typeof userInfo?.paidFeature === "string") dispatch(updateUserClaims([userInfo?.paidFeature]));
      else dispatch(updateUserClaims(userInfo?.paidFeature));

      if (userInfo.sub) {
        removeLottieLoading();
        setIsValidToken(true);
      } else {
        setIsValidToken(false);
      }
    } catch (e: any) {
      // If any error except Auth (401, 403) happens we should remove the loading to let the user reload the app
      if (!e.toString().includes("AuthError")) {
        removeLottieLoading();
      }
    }
  }, [token]);

  // this is for situations when dashboard is opened without token
  useEffect(() => {
    if (!loading && (!token || !isValidToken)) {
      userManager.signinRedirect();
    }
    // `token & isValidToken` should not be in deps section, because when we log out, we need app NOT TO redirect to signIn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!token || !isValidToken) {
    setTimeout(() => {
      setElement(
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <>
            <Typography.Title level={4}>{t("initApp.description")}</Typography.Title>
            <Button variant={"primary"} onClick={() => window.location.reload()}>
              {t("initApp.button")}
            </Button>
          </>
        </div>
      );
    }, 4000);
    return element;
  }

  return loading ? <Loading /> : <Main />;
};

export default InitApp;
